import { Textbit, type Element } from '@ttab/textbit'
import { LoadingText } from './LoadingText'
import { useDocumentSnapshot } from '@/hooks'
import { Bold, Italic, Link, Text, OrderedList, UnorderedList, TTVisual, Image, Factbox, Table } from '@ttab/textbit-plugins'
import { PreVersion } from './Version/PreVersion'
import { PreVersionInfo, type VersionInfo } from './Version/PreVersionInfo'
import type { JSX } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@ttab/elephant-ui/utils'
import { AlertDescription } from '@ttab/elephant-ui'
import { MessageCircleMoreIcon, TextIcon } from '@ttab/elephant-ui/icons'
import { View } from './View'
import { BaseEditor } from './Editor/BaseEditor'

const textStyle = `outline-none
  pt-4
  pb-4
  dark:text-slate-100
  grow
  max-w-(--breakpoint-xl)`

/**
 * `showFooter` renders the same language, word and character counts that the
 * editable editor shows, so a published or pinned article reads the same way.
 *
 * It brings its own View.Content with it, because the footer has to be that
 * content's sibling rather than sit inside it, and it needs a ViewProvider
 * above it since both are View parts. Render it directly under a View.Root.
 * `metaBar` goes inside that content, below the notes, so it scrolls with the text.
 */
export const Editor = ({ id, version, textOnly = false, direct, versionInfo, disableScroll = false, showNotes = false, showTitle = false, showFooter = false, metaBar }: {
  id: string
  textOnly?: boolean
  version?: bigint | undefined
  versionInfo?: VersionInfo
  direct?: boolean
  disableScroll?: boolean
  showNotes?: boolean
  showTitle?: boolean
  showFooter?: boolean
  metaBar?: JSX.Element
}): JSX.Element => {
  const { t, i18n } = useTranslation('editor')

  const activeLocale = i18n.resolvedLanguage

  const getPlugins = () => {
    const basePlugins = [Text, UnorderedList, OrderedList, Bold, Italic, Link, Table]
    return [
      ...basePlugins.map((initPlugin) => initPlugin()),
      TTVisual({
        captionLabel: t('image.captionLabel'),
        bylineLabel: t('image.bylineLabel'),
        removable: false
      }),
      Image({
        captionLabel: t('image.captionLabel'),
        bylineLabel: t('image.bylineLabel'),
        removable: false
      }),
      Factbox({
        headerTitle: t('factbox.headerTitle'),
        modifiedLabel: t('factbox.modifiedLabel'),
        createdLabel: t('factbox.createdLabel'),
        lastModifiedLabel: t('factbox.lastModifiedLabel'),
        footerTitle: t('factbox.footerTitle'),
        removable: false,
        locale: activeLocale
      })
    ]
  }

  const { data: document, error } = useDocumentSnapshot({ id, version, direct })

  if (error) {
    return <div>{t('errors:messages.loadFailed')}</div>
  }

  if (!document) {
    return (
      <LoadingText>
        {t('common:misc.loading')}
      </LoadingText>
    )
  }

  const content = document.content

  if (!content.length && document.meta && document.links) {
    // Preversion-preview: render non-article types of documents, such as Planning or Event
    return (
      <PreVersion
        content={document}
        versionInfo={versionInfo}
      />
    )
  }

  const notes = showNotes ? (document.meta['core/note'] ?? []) : []

  const heading = (
    <>
      {versionInfo && version && (
        <PreVersionInfo versionInfo={versionInfo} />
      )}

      {showTitle && document.title && (
        <p className='text-lg font-bold pt-2 ps-12 pe-12 '>
          {document.title}
        </p>
      )}

      {!!notes.length && (
        <div className='pe-12 ps-12 mt-6'>
          {notes.map((note, index) => {
            const isPublic = note.role === 'public'
            const NoteIcon = isPublic ? TextIcon : MessageCircleMoreIcon

            return (
              <div key={index} className='flex flex-row gap-2 w-full justify-between items-center'>
                <div className={cn(
                  'flex gap-3 py-3 px-4 items-center w-full border rounded',
                  isPublic
                    ? 'bg-blue-50 dark:bg-blue-800 border-blue-500/30 dark:border-blue-300/50'
                    : 'bg-yellow-50 dark:bg-yellow-800 border-yellow-500/30 dark:border-yellow-300/50'
                )}
                >
                  <NoteIcon
                    size={18}
                    strokeWidth={1.75}
                    className={cn('shrink-0 fill-[#ffffff] dark:fill-gray-800',
                      isPublic
                        ? 'stroke-blue-800/50 dark:stroke-blue-300'
                        : 'stroke-yellow-800/50 dark:stroke-yellow-300')}
                  />
                  <AlertDescription>{note.data?.text}</AlertDescription>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </>
  )

  // The counts come from the Textbit context, so the footer has to sit inside
  // the root. That makes the root the view's flex column, the same shape the
  // editable editor has: the content is width capped and scrolls, the footer is
  // its sibling and spans the full width, so its top border reaches the edges.
  if (showFooter) {
    return (
      <Textbit.Root
        key={`${id}-${version ?? 'latest'}`}
        value={filterText(content, textOnly)}
        plugins={getPlugins()}
        readOnly
        className='flex flex-col grow min-h-0'
      >
        <View.Content className='flex flex-col max-w-[1000px]' variant='grid'>
          <div className={cn('flex flex-col grow pb-6', !disableScroll && 'overflow-auto')}>
            {heading}

            {metaBar}

            {/* The text alone sits in the editable editor's wrapper. Notes and
                title stay outside it, the way they are in that view too. */}
            <div className='flex flex-col grow pr-12 max-w-(--breakpoint-xl)'>
              {/* No gutter here, so the text pads the gutter's own width (w-14)
                  instead of reserving it as margin the way the editable view does. */}
              <Textbit.Editable className={cn(textStyle, 'ps-14 pe-12')} />
            </div>
          </div>
        </View.Content>

        <View.Footer>
          <BaseEditor.Footer lang={document.language} />
        </View.Footer>
      </Textbit.Root>
    )
  }

  return (
    <div className={cn(
      'flex flex-col w-full pb-6 overflow-x-hidden max-w-(--breakpoint-lg) mx-auto',
      !disableScroll && 'overflow-y-auto'
    )}
    >
      {heading}

      <Textbit.Root
        key={`${id}-${version ?? 'latest'}`}
        value={filterText(content, textOnly)}
        plugins={getPlugins()}
        readOnly
      >
        <Textbit.Editable className={cn(textStyle, 'ps-12 pe-12 pr-12')} />
      </Textbit.Root>
    </div>

  )
}

function filterText(content: Element[], textOnly: boolean): Element[] {
  if (!textOnly) {
    return content
  }

  return content.filter((c) => c.type !== 'tt/visual' && c.type !== 'core/image')
}
